import type { IconDefinition } from '@hg-akademie-ban/ban-elevate-assets';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils.js';
import { Counter } from '@/components/ui/counter.js';
import { Icon } from '@/components/ui/icon.js';
import { LabelWrapper } from '@/components/ui/labelWrapper.js';
import { LoadingSpinner } from '@/components/ui/loadingSpinner.js';

type Variant = 'primary' | 'secondary' | 'tertiary' | 'primaryKnockout' | 'secondaryKnockout' | 'onAccentSubtle';

type Size = 'default' | 'compact';

const baseStyles = cn([
  // default
  'inline-flex',
  'items-center',
  'justify-center',
  'gap-8',
  'rounded-round',
  'whitespace-nowrap',
  'border',
  'transition-colors',
  'cursor-pointer',
  'select-none',
  // focus ring
  'outline-none',
  'focus-visible:outline-solid',
  'focus-visible:outline-2',
  'focus-visible:outline-offset-2',
  // disabled
  'disabled:pointer-events-none',
]);

const variantStyles: Record<Variant, string> = {
  primary: cn([
    // default
    'bg-button-primary-default',
    'text-button-primary-default',
    'border-button-primary-default',
    // hover
    'hover:bg-button-primary-hover',
    'hover:text-button-primary-hover',
    'hover:border-button-primary-hover',
    // active
    'active:bg-button-primary-active',
    'active:text-button-primary-active',
    'active:border-button-primary-active',
    // focus
    'focus-visible:bg-button-primary-focus',
    'focus-visible:text-button-primary-focus',
    'focus-visible:border-button-primary-focus',
    'focus-visible:outline-brand',
    // disabled
    'disabled:bg-button-primary-disabled',
    'disabled:text-button-primary-disabled',
    'disabled:border-button-primary-disabled',
  ]),

  secondary: cn([
    // default
    'bg-button-secondary-default',
    'text-button-secondary-default',
    'border-button-secondary-default',
    // hover
    'hover:bg-button-secondary-hover',
    'hover:text-button-secondary-hover',
    'hover:border-button-secondary-hover',
    // active
    'active:bg-button-secondary-active',
    'active:text-button-secondary-active',
    'active:border-button-secondary-active',
    // focus
    'focus-visible:bg-button-secondary-focus',
    'focus-visible:text-button-secondary-focus',
    'focus-visible:border-button-secondary-focus',
    'focus-visible:outline-brand',
    // disabled
    'disabled:bg-button-secondary-disabled',
    'disabled:text-button-secondary-disabled',
    'disabled:border-button-secondary-disabled',
  ]),

  tertiary: cn([
    // default
    'bg-button-tertiary-default',
    'text-button-tertiary-default',
    'border-button-tertiary-default',
    // hover
    'hover:bg-button-tertiary-hover',
    'hover:text-button-tertiary-hover',
    'hover:border-button-tertiary-hover',
    // active
    'active:bg-button-tertiary-active',
    'active:text-button-tertiary-active',
    'active:border-button-tertiary-active',
    // focus
    'focus-visible:bg-button-tertiary-focus',
    'focus-visible:text-button-tertiary-focus',
    'focus-visible:border-button-tertiary-focus',
    'focus-visible:outline-brand',
    // disabled
    'disabled:bg-button-tertiary-disabled',
    'disabled:text-button-tertiary-disabled',
    'disabled:border-button-tertiary-disabled',
  ]),

  primaryKnockout: cn([
    // default
    'bg-button-primary-knockout-default',
    'text-button-primary-knockout-default',
    'border-button-primary-knockout-default',
    // hover
    'hover:bg-button-primary-knockout-hover',
    'hover:text-button-primary-knockout-hover',
    'hover:border-button-primary-knockout-hover',
    // active
    'active:bg-button-primary-knockout-active',
    'active:text-button-primary-knockout-active',
    'active:border-button-primary-knockout-active',
    // focus
    'focus-visible:bg-button-primary-knockout-focus',
    'focus-visible:text-button-primary-knockout-focus',
    'focus-visible:border-button-primary-knockout-focus',
    'focus-visible:outline-knockout',
    // disabled
    'disabled:bg-button-primary-knockout-disabled',
    'disabled:text-button-primary-knockout-disabled',
    'disabled:border-button-primary-knockout-disabled',
  ]),

  secondaryKnockout: cn([
    // default
    'bg-button-secondary-knockout-default',
    'text-button-secondary-knockout-default',
    'border-button-secondary-knockout-default',
    // hover
    'hover:bg-button-secondary-knockout-hover',
    'hover:text-button-secondary-knockout-hover',
    'hover:border-button-secondary-knockout-hover',
    // active
    'active:bg-button-secondary-knockout-active',
    'active:text-button-secondary-knockout-active',
    'active:border-button-secondary-knockout-active',
    // focus
    'focus-visible:bg-button-secondary-knockout-focus',
    'focus-visible:text-button-secondary-knockout-focus',
    'focus-visible:border-button-secondary-knockout-focus',
    'focus-visible:outline-knockout',
    // disabled
    'disabled:bg-button-secondary-knockout-disabled',
    'disabled:text-button-secondary-knockout-disabled',
    'disabled:border-button-secondary-knockout-disabled',
  ]),

  onAccentSubtle: cn([
    // default
    'bg-button-primary-on-accent-subtle-default',
    'text-button-primary-on-accent-subtle-default',
    'border-button-primary-on-accent-subtle-default',
    // hover
    'hover:bg-button-primary-on-accent-subtle-hover',
    'hover:text-button-primary-on-accent-subtle-hover',
    'hover:border-button-primary-on-accent-subtle-hover',
    // active
    'active:bg-button-primary-on-accent-subtle-active',
    'active:text-button-primary-on-accent-subtle-active',
    'active:border-button-primary-on-accent-subtle-active',
    // focus
    'focus-visible:bg-button-primary-on-accent-subtle-focus',
    'focus-visible:text-button-primary-on-accent-subtle-focus',
    'focus-visible:border-button-primary-on-accent-subtle-focus',
    'focus-visible:outline-brand',
    // disabled
    'disabled:bg-button-primary-on-accent-subtle-disabled',
    'disabled:text-button-primary-on-accent-subtle-disabled',
    'disabled:border-button-primary-on-accent-subtle-disabled',
  ]),
};

const counterKnockoutByButtonVariant: Record<Variant, boolean> = {
  primary: true,
  secondary: false,
  tertiary: false,
  primaryKnockout: false,
  secondaryKnockout: true,
  onAccentSubtle: true,
};

const sizeStyles: Record<Size, string> = {
  default: 'h-48 px-24 label-lg',
  compact: 'h-36 py-8 px-16 label-default',
};

const buttonVariants = cva(baseStyles, {
  variants: {
    variant: variantStyles,
    size: sizeStyles,
  },
  defaultVariants: {
    variant: 'primary',
    size: 'default',
  },
});

type ButtonVisualProps = VariantProps<typeof buttonVariants> & {
  children?: never;
  label: string;
  leadingIcon?: IconDefinition;
  trailingIcon?: IconDefinition;
  counter?: string | number;
  loading?: boolean;
};

export type ButtonProps = ButtonVisualProps &
  (
    | (Omit<ComponentProps<'button'>, 'children'> & { href?: never })
    | (Omit<ComponentProps<'a'>, 'children'> & { disabled?: boolean })
  );

/** Button component with support for leading/trailing icons and an optional counter badge. The `variant` prop controls the visual style, while the `size` prop adjusts padding and typography. The component ensures accessibility with focus styles and disabled states.
 * @example
 * <Button label="Submit" variant="primary" size="default" leadingIcon={someIcon} trailingIcon={someIcon} counter={5} onClick={handleClick} />
 */
export function Button({
  ref,
  className,
  variant,
  // Mirrors CVA's defaultVariants — needed because CVA only resolves defaults internally for class generation,
  // but the component logic (e.g. conditional py-1 on LabelWrapper) requires `size` to be defined at runtime.
  size = 'default',
  label,
  leadingIcon,
  trailingIcon,
  counter,
  loading,
  disabled,
  onClick,
  href,
  ...props
}: ButtonProps) {
  const isDisabled = loading || disabled;
  const resolvedVariant = variant ?? 'primary';
  const sharedClassName = cn(buttonVariants({ variant, size, className }), loading && 'pointer-events-none relative');
  const content = (
    <>
      {/* Always render all content — invisible when loading (preserves width) */}
      <span className={cn('inline-flex items-center justify-center gap-8', loading && 'invisible')}>
        {leadingIcon ? (
          <Icon icon={leadingIcon} size="small" />
        ) : (
          loading && <LoadingSpinner variant="inherit" size="small" aria-hidden={true} role={undefined} />
        )}
        <LabelWrapper label={label} className={cn({ 'py-1': size === 'default' })} />
        {trailingIcon && <Icon icon={trailingIcon} size="small" />}
        {counter !== undefined && (
          <Counter number={counter} knockout={counterKnockoutByButtonVariant[resolvedVariant]} disabled={isDisabled} />
        )}
      </span>
      {/* Loading overlay — only rendered when loading */}
      {loading && (
        <span className="absolute inset-0 inline-flex items-center justify-center gap-8">
          <LoadingSpinner variant="inherit" size="small" aria-hidden={true} role={undefined} />
          <LabelWrapper label={label} className={cn({ 'py-1': size === 'default' })} />
        </span>
      )}
      {loading && <span className="sr-only">Loading</span>}
    </>
  );

  if (href !== undefined) {
    const anchorProps = props as Omit<ComponentProps<'a'>, 'children'>;

    return (
      <a
        ref={ref}
        data-slot="button"
        data-loading={loading || undefined}
        href={isDisabled ? undefined : href}
        className={sharedClassName}
        aria-disabled={isDisabled || undefined}
        aria-busy={loading || undefined}
        onClick={loading ? undefined : onClick}
        {...anchorProps}
      >
        {content}
      </a>
    );
  }

  const buttonProps = props as Omit<ComponentProps<'button'>, 'children'>;

  return (
    <button
      ref={ref as ComponentProps<'button'>['ref']}
      data-slot="button"
      data-loading={loading || undefined}
      className={sharedClassName}
      disabled={disabled && !loading}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      onClick={loading ? undefined : (onClick as ComponentProps<'button'>['onClick'])}
      {...buttonProps}
    >
      {content}
    </button>
  );
}
